import { OrganizationUser } from "@aura-frontend/omnibus-az-client";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { useAppContext } from "@/hooks/use-app-context";
import { removeOrgPrefix } from "@/lib/utils";
import {
  OrganizationRoles,
  organizationRolesHierarchy,
  RoleOptions,
} from "@/types/roles";
import { delay } from "@/utils/delay";

import { DeleteUserFromAccountButton } from "./components/DeleteUserFromAccountButton";

export const useColumns = () => {
  const { orgId: rawOrgId } = useAppContext();
  const orgId = rawOrgId ? removeOrgPrefix(rawOrgId) : "";
  const queryClient = useQueryClient();

  const columns: ColumnDef<OrganizationUser>[] = useMemo(
    () => [
      {
        accessorKey: "givenName",
        header: "First Name",
        size: 125,
        enableSorting: false,
      },
      {
        accessorKey: "familyName",
        header: "Last Name",
        size: 125,
        enableSorting: false,
      },
      {
        accessorKey: "emails",
        size: 300,
        header: "Emails",
        cell: ({ row: { original: row } }) => {
          return row.emails?.join(", ");
        },
        enableSorting: false,
      },
      {
        header: "Role",
        enableSorting: false,

        size: 200,
        accessorKey: "orgRoles",
        cell: ({ row: { original: row } }) => {
          if (row.orgRoles.includes("account_owner")) {
            return <p className="p-2">Account Owner</p>;
          }
          if (row.orgRoles.includes("unverified_account_owner")) {
            return <p className="p-2">Unverified Account Owner</p>;
          }

          const orgRole = organizationRolesHierarchy.find((role: string) =>
            row.orgRoles.includes(role as keyof typeof RoleOptions),
          ) as OrganizationRoles;

          return <p className="p-2">{RoleOptions[orgRole]}</p>;
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row: { original: row } }) => (
          <div className="flex space-x-2">
            <DeleteUserFromAccountButton
              orgId={orgId}
              accountId={row.userId}
              isAccountOwner={row.orgRoles.includes("account_owner")}
              onDelete={async () => {
                // data is not updated if we refetch instantly
                await delay(1000);
                queryClient.invalidateQueries({
                  queryKey: ["getOrganizationsByOrgIdUsers", orgId],
                });
              }}
            />
          </div>
        ),
      },
    ],
    [orgId, queryClient],
  );

  return { columns };
};
